/**
 * Configures Honeybadger error reporting. Runs as a deferred script after
 * honeybadger.min.js and before vendor.js/theme.js (deferred scripts execute
 * in document order), so uncaught errors thrown while theme code initializes
 * are captured. Settings come from window.CAPHoneybadger, set inline by
 * snippets/honeybadger.liquid.
 */
(function () {
  var config = window.CAPHoneybadger;
  if (!window.Honeybadger || !config) return;

  // Computed once: engines below the theme's syntax floor (optional chaining
  // and class fields, both used by FoxTheme's own vendor code) fail to parse
  // whole asset files, so every page load cascades unactionable errors —
  // ReferenceErrors for classes from dead files, undefined FoxTheme.config,
  // etc. Real browsers auto-update far past this floor; in practice these
  // are scrapers pinned to old headless builds (e.g. Chrome 75, fault
  // #132793955). Only a SyntaxError marks the engine unsupported — any other
  // failure (e.g. a CSP blocking eval) must not silently disable reporting.
  var belowSyntaxFloor = false;
  try {
    new Function('({})?.x; class CAPSyntaxProbe { y = 1 }');
  } catch (err) {
    belowSyntaxFloor = err instanceof SyntaxError;
  }

  window.Honeybadger.configure({
    apiKey: config.apiKey,
    environment: config.environment,
    revision: config.revision,
    // Caps reports per page load so a render-loop error can't flood the quota.
    maxErrors: 10
  });

  window.Honeybadger.beforeNotify(function (notice) {
    var stack = notice.stack || '';
    var message = notice.message || '';

    // Failed dynamic import() of a module the browser could not fetch. Only
    // Shopify's own inline scripts use import() on this storefront (Shop Pay
    // login / cart-sync loaders under /cdn/shopifycloud/ — the theme's assets
    // contain no import() calls at all), so every one of these is a fetch
    // that died on the shopper's side: navigation mid-load, flaky network,
    // ad-blocker (fault #132800931). Matched on wording alone, across the
    // three engines' phrasings, and without requiring the module URL: Safari
    // omits it ("Importing a module script failed."), and a notice for this
    // fault reached the dashboard after the URL-scoped version of this check
    // shipped, so the URL is not a signal worth gating on. Checked before the
    // stack filters because these errors carry no frames. If the theme ever
    // adopts import(), narrow this back to non-theme module URLs.
    if (
      /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(message + '\n' + stack)
    ) {
      return false;
    }

    // Only report errors originating in our own theme asset files
    // (/cdn/shop/t/<id>/assets/ or legacy cdn.shopify.com asset paths).
    // Drops errors from app embeds, HubSpot, Cometly, Shogun, and other
    // third-party scripts we can't fix. Inline <script> errors are also
    // excluded by this — loosen the pattern if those ever matter.
    if (!/\/cdn\/shop\/t\/[^\s)]*\/assets\/|cdn\.shopify\.com\/[^\s)]*\/assets\//.test(stack)) {
      return false;
    }

    // Attribute errors by their ORIGIN frame, not just stack membership: if
    // the top backtrace frame is not one of our assets, a third-party script
    // threw with our code merely on the call stack. Observed: an injected
    // script (about:blank frames) monkey-patching Element.addEventListener
    // and exploding when header.js registered listeners (fault #132784412,
    // "elm.events.push is not a function").
    var backtrace = notice.backtrace || [];
    if (
      backtrace.length &&
      backtrace[0].file &&
      !/\/cdn\/shop\/t\/[^\s)]*\/assets\/|cdn\.shopify\.com\/[^\s)]*\/assets\//.test(backtrace[0].file)
    ) {
      return false;
    }

    // Engines that cannot parse the theme's JS at all (see probe above).
    if (belowSyntaxFloor) {
      return false;
    }

    // Dead-dependency cascade: when theme.js never executed for this shopper
    // (its fetch failed or was blocked — observed on stale cached HTML with
    // month-old asset URLs, faults #133067596/#132779337), every later section
    // script throws at class-definition time ("DrawerComponent is not
    // defined", "AccordionDetails is not defined", reading FoxTheme.config/
    // utils off undefined...). Nothing in those reports is fixable — the only
    // failure is the asset fetch itself, which never surfaces as a JS error.
    // FoxTheme.config and FoxTheme.utils exist iff theme.js ran, so their
    // absence at notify time marks the cascade. Errors originating in the
    // scripts that legitimately run BEFORE theme.js (honeybadger-init.js,
    // vendor.js) — or in theme.js itself, if it ever aborts partway — are
    // kept: those are real bugs, not echoes of a missing file. A regression
    // that removes a base class while theme.js still executes also still
    // reports, because config/utils remain present.
    if (
      !(window.FoxTheme && window.FoxTheme.config && window.FoxTheme.utils) &&
      !(
        backtrace.length &&
        backtrace[0].file &&
        /\/assets\/(theme|vendor|honeybadger-init)(\.min)?\.js/.test(backtrace[0].file)
      )
    ) {
      return false;
    }

    // Browser extensions injecting into the page.
    if (/chrome-extension:|safari-extension:|moz-extension:/.test(stack)) {
      return false;
    }

    // Crawlers and scanner bots (AdsBot, Applebot, bingbot, Facebook's
    // link-preview crawlers, ...) execute only a subset of the page's
    // scripts, so cross-file references (FoxTheme.* globals from vendor.js,
    // base classes from theme.js) throw errors no real browser produces.
    // Unlike filtering by error shape, filtering by audience can't hide a
    // real-user bug: every error from a real browser still reports.
    // GoogleOther/Google-* cover Google crawlers whose tokens contain
    // neither "bot" nor "crawl" (observed slipping through: GoogleOther).
    if (/bot|crawl|spider|headless|facebookexternalhit|meta-external|googleother|google-|lighthouse|slurp|ia_archiver/i.test(navigator.userAgent)) {
      return false;
    }

    // Every real browser's UA has started with "Mozilla/" since the 1990s
    // (Chrome, Safari, Firefox, Edge, and webviews all keep the prefix for
    // compatibility). A UA without it is a non-browser client — curl,
    // python-requests, or homemade scrapers like the literal UA "pc" whose
    // own injected addEventListener hook blew the stack on our pages
    // (faults #132800929-31). Complements the token filter above: that one
    // catches bots that announce themselves, this one catches clients that
    // don't pretend to be a browser at all.
    if (!/^Mozilla\//.test(navigator.userAgent)) {
      return false;
    }

    // Benign browser noise: cross-origin "Script error." and the
    // ResizeObserver loop warning neither of which is actionable.
    if (/^Script error\.?$|ResizeObserver loop/.test(message)) {
      return false;
    }
  });

  window.Honeybadger.setContext(config.context || {});
})();
